import * as React from 'react'
import {
  CodeSnippet,
  CodeSnippetActions,
  CodeSnippetContainer,
  CodeSnippetFooter,
} from '~/sotano-kit/primitives/code-snippet'
import { Language, PrismTheme } from 'prism-react-renderer'
import { TabsList, Tabs, TabsTrigger } from '~/sotano-kit/primitives/tabs'
import { Icon } from 'basehub/react-icon'

type Snippet = {
  selectValue: string
  snippet: {
    language?: Language
    label: string
    icon: string
    code: string | ((...args: string[]) => string)
  }
  disabled?: boolean
}

export const MktCodeSnippet = <T extends string>({
  snippets,
  value,
  handleValueChange,
  links = [],
  snippetArgs = [],
  ...rest
}: {
  value: T
  handleValueChange?: (value: T) => void
  snippets: Snippet[]
  theme?: PrismTheme
  lineNumbers?: boolean
  links?: React.ComponentProps<typeof CodeSnippetFooter>['links'] | null
  snippetArgs?: string[]
}) => {
  const [selectedValue, setSelectedValue] = React.useState(value)
  const data = React.useMemo(
    () => snippets.find((item) => item.selectValue === selectedValue),
    [selectedValue, snippets]
  )

  if (!data) return null

  return (
    <CodeSnippetContainer>
      <CodeSnippetActions>
        <Tabs
          defaultValue={selectedValue}
          onValueChange={(v) => {
            handleValueChange?.(v as T)
            setSelectedValue(v as T)
          }}
        >
          <TabsList className="!p-0">
            {snippets.map((item) => (
              <TabsTrigger
                value={item.selectValue}
                key={item.selectValue}
                className="!min-w-0 h-9 text-xs px-3 font-medium focus-visible:outline focus-visible:outline-dark-control-base focus-visible:-outline-offset-4"
              >
                <Icon content={item.snippet.icon} />
                <span className="capitalize-first">{item.snippet.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </CodeSnippetActions>
      <CodeSnippet
        {...rest}
        code={
          typeof data.snippet.code === 'string'
            ? data.snippet.code
            : data.snippet.code(...snippetArgs)
        }
        language={data.snippet.language ?? 'bash'}
        className="p-4"
        lineNumbers
        unstyled
      />
      {!!links?.length && <CodeSnippetFooter links={links} />}
    </CodeSnippetContainer>
  )
}
